import React, {useState, useEffect, useCallback} from 'react';
import { usersApi } from '../api/api';
import './Users.css';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    role: '',
    isActive: true,
    jobTitle: '',
    department: '',
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState('displayName');
  const [sortOrder, setSortOrder] = useState('asc');

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll({
        page,
        limit,
        sortBy,
        sortOrder,
        search: searchTerm,
        role: roleFilter || undefined,
        active: activeFilter !== '' ? activeFilter : undefined,
      });
      setUsers(response.data.data);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, limit, page, roleFilter, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
    fetchStatistics();
  }, [page, limit, sortBy, sortOrder, searchTerm, roleFilter, activeFilter, fetchUsers]);

  const fetchStatistics = async () => {
    try {
      const response = await usersApi.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setRoleFilter('');
    setActiveFilter('');
    setPage(1);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      role: user.role,
      isActive: user.isActive,
      jobTitle: user.jobTitle || '',
      department: user.department || '',
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await usersApi.update(selectedUser._id, editFormData);
      setShowModal(false);
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user. Please try again.');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive) {
        await usersApi.deactivate(user._id);
      } else {
        await usersApi.activate(user._id);
      }
      fetchUsers();
      fetchStatistics();
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Error updating user status. Please try again.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user ${user.displayName}? This action cannot be undone.`)) {
      try {
        await usersApi.delete(user._id);
        fetchUsers();
        fetchStatistics();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user. Please try again.');
      }
    }
  };

  const getRoleBadgeClass = (role) => {
    return role === 'admin' ? 'role-admin' : 'role-user';
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('hu-HU');
  };

  if (loading && !users.length) {
    return <div className="card">Loading...</div>;
  }

  return (
    <div className="users-page">
      <div className="card">
        <div className="header-row">
          <h2>Users ({total})</h2>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="statistics-grid">
            <div className="stat-card">
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{statistics.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active</div>
              <div className="stat-value stat-success">{statistics.active}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Inactive</div>
              <div className="stat-value stat-warning">{statistics.inactive}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Admins</div>
              <div className="stat-value stat-info">{statistics.admins}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Recent Logins (7d)</div>
              <div className="stat-value">{statistics.recentLogins}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>

          <div className="filter-controls">
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              className="filter-select"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>

            <select
              value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {(searchTerm || roleFilter || activeFilter) && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('displayName')} style={{ cursor: 'pointer' }}>
                  Name{getSortIcon('displayName')}
                </th>
                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                  Email{getSortIcon('email')}
                </th>
                <th onClick={() => handleSort('jobTitle')} style={{ cursor: 'pointer' }}>
                  Job Title{getSortIcon('jobTitle')}
                </th>
                <th onClick={() => handleSort('department')} style={{ cursor: 'pointer' }}>
                  Department{getSortIcon('department')}
                </th>
                <th onClick={() => handleSort('role')} style={{ cursor: 'pointer' }}>
                  Role{getSortIcon('role')}
                </th>
                <th onClick={() => handleSort('isActive')} style={{ cursor: 'pointer' }}>
                  Status{getSortIcon('isActive')}
                </th>
                <th onClick={() => handleSort('lastLogin')} style={{ cursor: 'pointer' }}>
                  Last Login{getSortIcon('lastLogin')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.displayName}</td>
                  <td>{user.email}</td>
                  <td>{user.jobTitle || '-'}</td>
                  <td>{user.department || '-'}</td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(user.lastLogin)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleEditUser(user)}
                      >
                        Edit
                      </button>
                      <button
                        className={`btn btn-sm ${user.isActive ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteUser(user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination-container">
          <div className="pagination-info">
            <label>
              Rows per page:
              <select value={limit} onChange={handleLimitChange}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </label>
            <span>
              Page {page} of {totalPages} (Total: {total} users)
            </span>
          </div>
          <div className="pagination-buttons">
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(1)}
              disabled={page === 1}
            >
              First
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
            <button
              className="btn btn-sm"
              onClick={() => handlePageChange(totalPages)}
              disabled={page === totalPages}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit User: {selectedUser.displayName}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  value={editFormData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={editFormData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={editFormData.jobTitle}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  name="department"
                  value={editFormData.department}
                  onChange={handleInputChange}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-success">
                  Save Changes
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
